import React, { useMemo } from "react";
import { Box, Paper, Stack, Typography, Button, Divider } from "@mui/material";
import { XIcon } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { useCheckoutForm } from "./hooks/useCheckoutForm";
import { useTheme } from "../../util/themes";

const formatCurrency = (value: number) =>
    `$${value.toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const CheckoutBlocked: React.FC = () => {
    const navigate = useNavigate();
    const theme = useTheme();

    const {
        isCartEmpty,
        minPurchaseAmount,
        minPurchaseSubtotal,
        minPurchaseMissing,
    } = useCheckoutForm();

    const title = useMemo(() => {
        return isCartEmpty ? "Tu carrito está vacío" : "No alcanzás el mínimo de compra";
    }, [isCartEmpty]);

    const subtitle = useMemo(() => {
        return isCartEmpty
            ? "Agregá productos al carrito para continuar con el checkout."
            : "Sumá más productos para poder continuar.";
    }, [isCartEmpty]);

    const handleBackToStore = () => navigate("/");

    return (
        <Box
            sx={{
                width: "100%",
                minHeight: "60vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                px: 2,
                py: 4,
            }}
        >
            <Paper
                elevation={0}
                sx={{
                    width: "100%",
                    maxWidth: 560,
                    borderRadius: theme.visual.borderRadius["2xl"],
                    border: "1px solid",
                    borderColor: "grey.300",
                    p: { xs: 2.5, sm: 4 },
                    backgroundColor: theme.colors.white,
                }}
            >
                <Stack spacing={2} alignItems="center">
                    <XIcon size={34} color={theme.colors.error} />

                    <Typography
                        fontFamily={theme.typography.fontFamily.primary}
                        fontSize={theme.typography.fontSize["2xl"]}
                        fontWeight={theme.typography.fontWeight.bold}
                        color={theme.colors.error}
                        textAlign="center"
                    >
                        {title}
                    </Typography>

                    <Typography
                        fontFamily={theme.typography.fontFamily.primary}
                        fontSize={theme.typography.fontSize.base}
                        fontWeight={theme.typography.fontWeight.light}
                        sx={{ opacity: 0.8, textAlign: "center" }}
                    >
                        {subtitle}
                    </Typography>

                    <Divider sx={{ width: "100%", borderColor: "grey.200", my: 1 }} />

                    <Stack spacing={1.25} sx={{ width: "100%" }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography
                                fontFamily={theme.typography.fontFamily.primary}
                                fontSize={theme.typography.fontSize.base}
                                fontWeight={theme.typography.fontWeight.normal}
                                sx={{ opacity: 0.8 }}
                            >
                                Subtotal actual
                            </Typography>
                            <Typography
                                fontFamily={theme.typography.fontFamily.primary}
                                fontSize={theme.typography.fontSize.base}
                                fontWeight={theme.typography.fontWeight.semibold}
                            >
                                {formatCurrency(minPurchaseSubtotal)}
                            </Typography>
                        </Stack>

                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography
                                fontFamily={theme.typography.fontFamily.primary}
                                fontSize={theme.typography.fontSize.base}
                                fontWeight={theme.typography.fontWeight.normal}
                                sx={{ opacity: 0.8 }}
                            >
                                Mínimo de compra
                            </Typography>
                            <Typography
                                fontFamily={theme.typography.fontFamily.primary}
                                fontSize={theme.typography.fontSize.base}
                                fontWeight={theme.typography.fontWeight.semibold}
                            >
                                {formatCurrency(minPurchaseAmount)}
                            </Typography>
                        </Stack>

                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography
                                fontFamily={theme.typography.fontFamily.primary}
                                fontSize={theme.typography.fontSize.base}
                                fontWeight={theme.typography.fontWeight.normal}
                                sx={{ opacity: 0.8 }}
                            >
                                Te falta
                            </Typography>
                            <Typography
                                fontFamily={theme.typography.fontFamily.primary}
                                fontSize={theme.typography.fontSize.base}
                                fontWeight={theme.typography.fontWeight.bold}
                                color={theme.colors.error}
                            >
                                {formatCurrency(minPurchaseMissing)}
                            </Typography>
                        </Stack>
                    </Stack>

                    <Button
                        variant="contained"
                        onClick={handleBackToStore}
                        sx={{
                            mt: 2,
                            width: "100%",
                            backgroundColor: theme.colors.primary,
                            color: theme.colors.white,
                            fontFamily: theme.typography.fontFamily.primary,
                            fontWeight: theme.typography.fontWeight.medium,
                            textTransform: "none",
                            py: 1.5,
                            borderRadius: theme.visual.borderRadius.xl,
                            "&:hover": { backgroundColor: theme.colors.black },
                        }}
                    >
                        VOLVER A LA TIENDA
                    </Button>
                </Stack>
            </Paper>
        </Box>
    );
};














